/**
 * Renko Rotations chart indicator configuration.
 *
 * Mirrors the Planning chart's curated IndicatorSidePanel (see ./planningIndicators)
 * but DROPS the Per-candle Profiles group (renko bricks have no per-candle profile),
 * keeping Volume (session VP/TPO + VWAP), Options / OI and Session Levels so the
 * renko chart carries the same confluence indicators with the same side-nav layout.
 *
 * NOTE: the renko x-axis is not time-uniform and its timeframe ("renko 8pt") is not a
 * backend candle timeframe, so the price-based level indicators (Session Levels, OI
 * Levels, Expected Move; fed by the timeframe-independent useLevelIndicators) render
 * exactly, while the time-anchored items (VP/TPO/VWAP/markers/exposure) render only
 * where their data + anchors line up. The toggles are present per the full-port choice.
 */

import { INDICATOR_GROUPS } from '../components/IndicatorSidePanel';
import { OVERLAY_STATE_IDS } from '../indicators/underlyingOverlays';

// Shared Session option set offered by both the session VP and session TPO.
const SESSION_OPTIONS = [
  { value: 'full', label: 'Full' },
  { value: 'eth-rth', label: 'ETH/RTH' },
  { value: 'rth', label: 'RTH' },
];

const baseGroup = (id) => INDICATOR_GROUPS.find((g) => g.id === id);

// Volume group: reuse delta/vwap verbatim, but rebuild the VP and TPO entries
// with a single Session picker each (identical to the planning chart's treatment).
// CVD is dropped -- the renko chart does not carry a CVD subplot.
const baseVolume = baseGroup('volume');
const volumeGroup = {
  ...baseVolume,
  indicators: baseVolume.indicators.filter((ind) => ind.id !== 'cvd').map((ind) => {
    if (ind.id === 'volumeProfile') {
      return {
        id: ind.id,
        label: ind.label,
        description: ind.description,
        hasModePicker2: true,
        modePickerId2: 'vpSessionScope',
        modeOptions2: SESSION_OPTIONS,
        hasSubToggle: true, subToggleId: 'nakedPOC',
        subToggleLabels: ['Naked POC', 'Naked POC'],
        subToggleDesc: ['Show untested settled-session POCs', 'Untested POCs visible'],
        subToggles: [
          { id: 'hvnZones', label: 'HVN Zones',
            desc: ['Show high volume node bands (last 5 RTH sessions)', 'High volume node bands visible'] },
        ],
      };
    }
    if (ind.id === 'tpoProfile') {
      return {
        id: ind.id,
        label: ind.label,
        description: 'Market Profile bars with POC and value area',
        hasModePicker2: true,
        modePickerId2: 'tpoSessionScope',
        modeOptions2: SESSION_OPTIONS,
        hasSubToggle: true, subToggleId: 'nakedTpoPOC',
        subToggleLabels: ['Naked TPO POC', 'Naked TPO POC'],
        subToggleDesc: ['Show untested settled-session TPO POCs', 'Untested TPO POCs visible'],
      };
    }
    return ind;
  }),
};

// Renko rotation-overlay controls (moved here from the top toolbar so every toggle
// lives in one side panel). On by default -- the rotation overlay is the chart's
// primary layer. The plugin store keys stay showLegs/showReach/showBias; these ids
// map to them in RenkoRotationsPage.
const rotationsGroup = {
  id: 'rotations',
  label: 'Rotations',
  color: '#a78bfa',
  description: 'Renko rotation overlay: zig-zag legs, 50/70/90 reach lines, directional bias',
  indicators: [
    { id: 'rotLegs', label: 'Legs', description: 'Zig-zag rotation legs + pivots. With 50/70/90 on, leg style shows reach tier: thin dotted = short (<= p70), normal solid = typical (> p70), bold solid = extended (> p90).' },
    { id: 'rotReach', label: '50/70/90', description: 'Per-pivot 50/70/90 leg-size reach lines + forward projection. Also styles each leg by reach tier vs p70/p90 (thin dotted / solid / bold).' },
    { id: 'rotBias', label: 'Bias', description: 'Top directional-bias ribbon (HH/HL structure, descriptive)' },
    { id: 'rotGauge', label: 'Gauge', description: 'Live retracement gauge: share of the reversal threshold retraced (descriptive)' },
  ],
};

// Options / OI group: reuse verbatim but DROP the time-keyed 'exposure' pane and
// the 'iv' line. The renko x-axis is de-collided brick seconds, so time-keyed
// series (the exposure pane's BaselineSeries, the IV line) would inject foreign
// bar indices and distort brick spacing (same reason CVD/VWAP are treated
// specially on the renko chart). Hedging Demand stays: it is a canvas overlay
// whose off-grid columns skip via timeToCoordinate null, the same class as the
// already-shipped Exposure Profile.
const baseOptions = baseGroup('options');
const optionsGroup = {
  ...baseOptions,
  indicators: baseOptions.indicators
    // The whole Index Overlays group is stripped (registry-driven, parent
    // row included): RenkoRotationsPage never passes the quoteOverlays
    // prop, so the rows would be dead switches here (the overlays are
    // minute-grid series that do not fit renko time anyway).
    .filter((ind) => ind.id !== 'iv' && ind.id !== 'showIvSkew' && ind.id !== 'showOptionsVolume'
      && !OVERLAY_STATE_IDS.includes(ind.id))
    // The Exposure pane rides as a sub-toggle of Exposure Profile; strip it
    // here (same time-keyed BaselineSeries reason its old standalone entry
    // was filtered) so renko never renders a dead switch.
    .map((ind) => (ind.id === 'showExposureProfile'
      ? { ...ind, subToggles: (ind.subToggles || []).filter((sub) => sub.id !== 'exposure') }
      : ind)),
};

// Curated renko sidebar: rotations + volume + options/OI + session levels (no per-candle profiles).
export const RENKO_GROUPS = [
  rotationsGroup,
  volumeGroup,
  optionsGroup,
  baseGroup('compare'),
  baseGroup('levels'),
];

// Everything off by default so the renko chart opens on the rotation overlay alone; the
// sub-toggle children (prior-level kinds, greek/distribution toggles) default on.
export const RENKO_INDICATOR_DEFAULTS = {
  // Rotation overlay (on by default; the renko chart's primary layer)
  rotLegs: true,
  rotReach: true,
  rotBias: true,
  rotGauge: true,
  // Options / OI context (drives the OI fetches for the Options group)
  expiryMode: '0dte',
  underlying: 'spx',
  oiData: 'settled',
  // Session profiles
  volumeProfile: false,
  nakedPOC: false,
  nakedTpoPOC: false,
  hvnZones: false,
  vpSessionScope: 'full',
  deltaProfile: false,
  tpoProfile: false,
  tpoSessionScope: 'full',
  vwap: false,
  vwapSessionMode: 'full',
  vwapBand1: false,
  vwapBand2: false,
  // Session levels (off by default; children of priorLevels default-on)
  priorLevels: false,
  prevDayLevels: true,
  priorWeekLevels: true,
  priorMonthLevels: true,
  overnightLevel: false,
  asiaLevels: false,        // Child of overnightLevel (gated by parent)
  londonLevels: false,      // Child of overnightLevel (gated by parent)
  openingRange: false,
  initialBalance: false,
  openingPrints: false,
  pivotPoints: false,
  sessionMarkers: false,
  // Options / OI (off by default; greek/distribution sub-toggles default-on)
  zeroLevels: false,
  zeroLevelsGex: true,
  zeroLevelsDex: true,
  zeroLevelsVex: true,
  zeroLevelsCex: true,
  gammaLevels: false,
  gammaLevelsGvwap: true,
  gammaLevelsBand: true,
  gammaLevelsWalls: true,
  showExposureProfile: false,
  expProfileGex: true,
  expProfileDex: true,
  expProfileVex: true,
  expProfileCex: true,
  exposureProfileMode: 'dealer',
  showExpProfileHeatmap: false,
  showExpProfileT1: false,
  expProfileAnchorFrac: null,    // drag-to-place open-session profile axis (null = left-edge pin; renko today-axis stays candle-anchored)
  expProfileT1AnchorFrac: null,  // drag-to-place T+1 profile axis (null = right-edge pin)
  hedgeDemandAnchorFrac: null,   // drag-to-place Hedging Demand axis (renko stays candle-anchored like the today axis)
  oiProfileAnchorFrac: null,     // drag-to-place OI distribution axis (unused on renko: axis stays candle-anchored)
  hedgingDemand: false,
  showHedgeDemandHeatmap: false,
  hedgeDemandRealized: false,
  showIvSkew: false,
  ivSkewAnchorFrac: null,       // drag-to-place IV Skew axis (null = offset default pin)
  ivSkewBand: true,             // inert on renko (smile filtered out of the panel)
  ivSkewShape: false,             // inert on renko (parent showIvSkew filtered out below)
  ivRealized: false,             // inert on renko ('iv' filtered out below)
  showForwardOi: false,
  showOiDistribution: false,
  oiDistCallOi: true,
  oiDistPutOi: true,
  oiDistNet: true,
  showOptionsVolume: false,      // inert on renko (entry filtered out of the panel above)
  optVolCalls: true,
  optVolPuts: true,
  optVolNet: true,
  optVolCumulative: false,
  optVolSplit: false,
  optVolFlowNet: false,
  optVolFlowPane: false,
  optVolAnchorFrac: null,        // inert on renko
  optVolCumAnchorFrac: null,     // inert on renko
  showUnderlyingCompare: false,      // Underlying Compare strip: price-anchored DOM strip, works on renko
  underlyingCompareMode: 'oi',       // Compare strip fill: 'oi' | 'exposure' | 'volume'
  underlyingCompareGreek: 'gex',     // Compare strip exposure-mode greek
  underlyingCompareZeroLevels: false, // Compare strip: per-lane zero-crossing ticks
  underlyingCompareGammaLevels: false, // Compare strip: per-lane GVWAP + walls
  showProjectedCurves: false,
  projGex: true,
  projDex: true,
  projVex: true,
  projCex: true,
  expectedMove: false,
};
